/**
 * Every backend error has the same envelope (docs/frontend/README.md §Errors):
 *   { "error": { "code": "otp_invalid", "message": "...", "request_id": "3f9c…" } }
 * The UI branches on `code`, never on `message`.
 */
export interface ApiErrorEnvelope {
  error: { code: string; message?: string; request_id?: string }
}

/** Codes the backend documents today. Unknown codes stay valid strings. */
export const API_ERROR_CODES = [
  'validation_failed',
  'unauthorized',
  'invalid_credentials',
  'password_change_required',
  'forbidden',
  'customer_blocked',
  'not_found',
  'conflict',
  'protected',
  'in_use',
  'otp_invalid',
  'otp_expired',
  'account_locked',
  'rate_limited',
  'internal',
] as const

export type ApiErrorCode = (typeof API_ERROR_CODES)[number]

export interface ApiErrorInit {
  code: string
  status: number
  message?: string
  /** Shown in error toasts so support can find the request in Grafana. */
  requestId?: string
}

export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly requestId: string | undefined

  constructor(init: ApiErrorInit) {
    super(init.message ?? init.code)
    this.name = 'ApiError'
    this.code = init.code
    this.status = init.status
    this.requestId = init.requestId
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError
}

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope['error'] | null> {
  try {
    const body: unknown = await response.json()
    const error = (body as ApiErrorEnvelope | null)?.error
    if (error && typeof error.code === 'string') return error
    return null
  } catch {
    return null
  }
}

/** Turns any failed Response into an ApiError, even a non-JSON one. */
export async function toApiError(response: Response): Promise<ApiError> {
  const envelope = await readEnvelope(response)
  return new ApiError({
    code: envelope?.code ?? `http_${response.status}`,
    status: response.status,
    message: envelope?.message,
    requestId: envelope?.request_id,
  })
}
